import React, { useState, useMemo, useEffect } from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../context/StateContext";
import { Error } from "../../../../../models/error";
import { Option } from "../../../../../models/option";
import { User } from "../../../../../models/user";
import { getTypes } from "../../../../../pages/private/Shared/Settings/People/Users/types";
import UserService from "../../../../../services/user.service";
import { Status } from "../../../../../util/enums";
import AutoSuggest from "../../../../Form/AutoSuggest";
import Checkbox from "../../../../Form/Checkbox";
import Input from "../../../../Form/Input";
import Select from "../../../../Form/Select";
import SelectMultiple from "../../../../Form/SelectMultiple";
import SkeletonComponent from "../../../Skeleton";
import { useActions } from "../../hooks/useActions";
import useStyles from "./styles";

interface Props {
  isOnProfilePage?: boolean;
  view: boolean;
  isLoading: boolean;
  userForm: User | null;
  classes: any;
  sequence?: string;
  managerName: string;
  getErrorByField: (value: string) => string | undefined;
  updateOrCreate: (attribute: string, value: any) => void;
  showingFromPeopleSettings?: boolean;
  isUserDriverType?: boolean;
  errorResponse?: Error;
}

const UserDetailsComponent = ({
  isOnProfilePage = false,
  managerName,
  sequence,
  view,
  isLoading,
  userForm,
  getErrorByField,
  updateOrCreate,
  showingFromPeopleSettings,
  isUserDriverType = false,
  errorResponse,
}: Props) => {
  const { t: translate } = useTranslation();
  const { currentUser, isSupplierPortal, isFMVersion, isSPVersion, isMovingVersion } =
    useAppState();
  const isSupply = isSupplierPortal || isFMVersion || isSPVersion;
  const { getApplications, getAllFleet } = useActions();

  const styles = useStyles();

  const [oldUsername, setOldUsername] = useState<string>("");

  const [managers, setManagers] = useState<User[]>([]);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");

  const { data: applications, isLoading: loadingApplications } = getApplications();
  const { data: fleets, isLoading: loadingFleets } = getAllFleet(isUserDriverType);

  useQuery(
    ["manager.list"],
    () =>
      UserService.getAll({
        include: JSON.stringify([
          {
            association: "usersCompanies",
            required: true,
            where: { companyId: currentUser?.companyId, status: Status.ACTIVE },
          },
        ]),
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !isOnProfilePage,
      onSuccess: (response) => {
        if (response && response.data && response.data?.length > 0) setManagers(response.data);
      },
    }
  );

  const userStatus: Option[] = useMemo(
    () => [
      { value: Status.ACTIVE, name: Status.ACTIVE },
      { value: Status.INACTIVE, name: Status.INACTIVE },
    ],
    []
  );

  useEffect(() => {
    if (userForm) setOldUsername(userForm?.username || "");
  }, []);

  useEffect(() => {
    if (sequence && !userForm?.code) {
      updateOrCreate("code", sequence);
    }
  }, [sequence]);

  const SIZE_MD = useMemo(
    () => (showingFromPeopleSettings && !isOnProfilePage ? 10 : 12),
    [showingFromPeopleSettings]
  );
  const SIZE_LG = useMemo(
    () => (showingFromPeopleSettings && !isOnProfilePage ? 10 : 12),
    [showingFromPeopleSettings]
  );

  const SIZE_PROFILE = useMemo(() => (isOnProfilePage ? 3 : 4), [isOnProfilePage]);

  const renderTypeOfUser = () => {
    const types = getTypes(isMovingVersion, isFMVersion);

    return types?.map((type) => {
      return (
        <MenuItem data-cy={type.name} key={type.name} value={type.value}>
          {type.name}
        </MenuItem>
      );
    });
  };

  const renderFleetOptions = () => {
    if (!fleets?.length) return null;

    return fleets.map((fleet) => {
      return (
        <MenuItem key={fleet.id} value={fleet.id}>
          {`${fleet.fleetNumber || ""} - ${fleet.model || ""}`}
        </MenuItem>
      );
    });
  };

  return (
    <>
      <Grid container direction="row" alignItems="flex-start" spacing={2}>
        <Grid
          item
          container
          direction="row"
          alignItems="flex-start"
          md={SIZE_MD}
          lg={SIZE_LG}
          spacing={2}
        >
          <Grid item xs={12} sm={SIZE_PROFILE} md={SIZE_PROFILE} lg={SIZE_PROFILE}>
            <SkeletonComponent isLoading={isLoading}>
              <>
                <Input
                  disabled={view}
                  label={translate("username")}
                  autoComplete="off"
                  name="username"
                  data-cy="username"
                  error={!!getErrorByField("username")}
                  errorMessage={getErrorByField("username")}
                  placeholder={translate("username")}
                  value={userForm?.username || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateOrCreate("username", e.target.value)
                  }
                />
                <Checkbox
                  data-cy="checkboxToUseEmailLikeUsername"
                  label={translate("useEmailAddress")}
                  className="mt-2"
                  checked={
                    userForm && userForm.email ? userForm.email === userForm.username : false
                  }
                  disabled={view}
                  onChange={(_, checked) => {
                    if (checked && userForm?.email) {
                      setOldUsername(userForm?.username || "");
                      updateOrCreate("username", userForm?.email);
                    } else if (!checked) {
                      if (oldUsername === userForm?.email) updateOrCreate("username", "");
                      else updateOrCreate("username", oldUsername);
                    }
                  }}
                />
              </>
            </SkeletonComponent>
          </Grid>
          {!isOnProfilePage && (
            <Grid item xs={12} sm={4} md={4} lg={4}>
              <SkeletonComponent isLoading={isLoading}>
                <Select
                  disabled={view}
                  label={translate("status")}
                  name="status"
                  data-cy="userStatus"
                  value={userForm?.status || ""}
                  error={!!getErrorByField("status")}
                  errorMessage={getErrorByField("status")}
                  onChange={(e: SelectChangeEvent<any>) => updateOrCreate("status", e.target.value)}
                >
                  {userStatus?.map((status) => (
                    <MenuItem data-cy={status.name} key={status.name} value={status.value}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </SkeletonComponent>
            </Grid>
          )}
          <Grid item xs={12} sm={SIZE_PROFILE} md={SIZE_PROFILE} lg={SIZE_PROFILE}>
            <SkeletonComponent isLoading={isLoading}>
              <Input
                disabled={view}
                label={translate("userCode")}
                name="user_code"
                autoComplete="off"
                data-cy="userCode"
                placeholder={translate("enterUserCode")}
                value={userForm?.code}
                error={!!getErrorByField("code")}
                errorMessage={getErrorByField("code")}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateOrCreate("code", e.target.value)
                }
              />
            </SkeletonComponent>
          </Grid>
        </Grid>
      </Grid>
      {!isOnProfilePage && (
        <>
          <Grid
            container
            direction="row"
            alignItems="flex-start"
            spacing={2}
            className={styles.mt12}
          >
            <Grid
              item
              container
              direction="row"
              alignItems="flex-start"
              md={SIZE_MD}
              lg={SIZE_LG}
              spacing={2}
            >
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <SkeletonComponent isLoading={isLoading}>
                  <Select
                    disabled={view}
                    label={translate("typeOfUser")}
                    error={!!getErrorByField("typeUser")}
                    errorMessage={getErrorByField("typeUser")}
                    name="type_of_user"
                    data-cy="typeOfUser"
                    value={userForm?.typeUser || ""}
                    onChange={(event: SelectChangeEvent<any>) =>
                      updateOrCreate("typeUser", event.target.value)
                    }
                  >
                    {renderTypeOfUser()}
                  </Select>
                </SkeletonComponent>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <SkeletonComponent isLoading={isLoading}>
                  <AutoSuggest
                    disabled={view}
                    label={isSupply ? translate("reportsTo") : translate("manager")}
                    placeholder={
                      isSupply ? translate("searchForUser") : translate("searchForManager")
                    }
                    options={managers}
                    data-cy="manager"
                    inputValue={managerName}
                    getOptionLabel={(manager?: User) => {
                      const name = manager?.name || "";
                      const lastName = manager?.lastName || "";
                      const email = manager?.email || "";

                      return `${name} ${lastName} - ${email}`;
                    }}
                    getOptionSelected={(option: User, value: User) => option.id === value.id}
                    onChange={(value: User) => {
                      if (value) {
                        updateOrCreate("manager", value);
                      } else updateOrCreate("manager", null);
                    }}
                  />
                </SkeletonComponent>
              </Grid>
              <Grid item xs={12} sm={4} md={4} lg={4}>
                <SkeletonComponent isLoading={isLoading}>
                  <SelectMultiple
                    disabled={view}
                    name="accessTo"
                    label={translate("accessTo")}
                    value={userForm?.applications?.map((option) => option.id || -2) || []}
                    selecteds={
                      userForm?.applications?.map((option) => ({
                        key: Number(option.id),
                        label: translate(option.name),
                      })) || []
                    }
                    loading={loadingApplications}
                    renderInternalCount={false}
                    onChange={(event: SelectChangeEvent<any>) => {
                      if (event.target.value.includes(-2) || event.target.value.includes(0)) return;

                      const options = applications?.data?.filter((option) =>
                        event.target.value.includes(option.id)
                      );

                      const applicationsLength = userForm?.applications?.length ?? 0;

                      if (options && applicationsLength !== options.length) {
                        updateOrCreate("applications", options);
                      }
                    }}
                    onAddAll={() => {
                      updateOrCreate("applications", applications?.data);
                    }}
                    onRemoveAll={() => {
                      updateOrCreate("applications", []);
                    }}
                    items={
                      applications?.data?.map((option) => ({
                        key: Number(option.id),
                        label: translate(option.name),
                      })) || []
                    }
                  />
                </SkeletonComponent>
              </Grid>
              {isUserDriverType && (
                <Grid item xs={12} sm={4} md={4} lg={4}>
                  <SkeletonComponent isLoading={loadingFleets}>
                    <Select
                      disabled={view}
                      label={translate("fleet")}
                      name="user_fleet"
                      data-cy="userFleet"
                      value={userForm?.fleet?.length ? userForm.fleet[0].id : ""}
                      error={!!getErrorByField("fleet") || !!errorResponse}
                      errorMessage={getErrorByField("fleet")}
                      errorResponse={errorResponse}
                      onChange={(event: SelectChangeEvent<any>) => {
                        const fleet = fleets?.find(
                          (fleet) => fleet.id === Number(event.target.value)
                        );

                        updateOrCreate("fleet", [fleet]);
                      }}
                    >
                      {renderFleetOptions()}
                    </Select>
                  </SkeletonComponent>
                </Grid>
              )}
            </Grid>
          </Grid>
          {isMovingVersion && (
            <SkeletonComponent isLoading={isLoading}>
              <Grid container direction="row" alignItems="flex-start" className={styles.mt24}>
                <Grid item className="mr-2">
                  <Checkbox
                    data-cy="checkboxLeader"
                    checked={!!userForm?.leader}
                    disabled={view}
                    onChange={(_, checked) => {
                      updateOrCreate("leader", checked);
                    }}
                  />
                </Grid>
                <Grid item xs={10} sm={9} md={9} lg={9} container direction="column">
                  <Grid item>
                    <span className={styles.leaderTtile}>{translate("thisUserIsALeader")}</span>
                  </Grid>
                  <Grid item>
                    <span className={styles.leaderDescription}>
                      {translate("checkThisBoxIfTheUserAboveIsAlsoALeader")}
                    </span>
                  </Grid>
                </Grid>
              </Grid>
            </SkeletonComponent>
          )}
        </>
      )}
    </>
  );
};

export default UserDetailsComponent;
